import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import type { components } from "../../generated/api.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];

/**
 * Ordnet ein Angebot einer Gruppe zu. Der Wert wechselt erst, wenn der Server ihn
 * bestätigt; bis dahin ist die Auswahl gesperrt.
 */
export function GroupSelect({ service, groups }: { service: ServiceDTO; groups: ServiceGroupDTO[] }): JSX.Element {
  const queryClient = useQueryClient();
  const move = useMutation({
    mutationFn: (groupId: string | null) =>
      apiRequest<ServiceDTO>(`/services/${encodeURIComponent(service.id)}`, { method: "PUT", body: { groupId } }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["services"] }),
        queryClient.invalidateQueries({ queryKey: ["service-groups"] }),
      ]),
  });

  return (
    <label className="grid gap-0.5 text-[11.5px] font-bold text-ink-3">
      Gruppe
      <select
        className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[13.5px] text-ink"
        value={service.groupId ?? ""}
        disabled={move.isPending}
        aria-busy={move.isPending}
        onChange={(event) => move.mutate(event.target.value || null)}
      >
        <option value="">Ohne Gruppe</option>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
      {move.error && (
        <span className="font-normal text-danger" role="alert">
          {move.error instanceof ApiRequestError ? move.error.message : "Nicht gespeichert."}
        </span>
      )}
    </label>
  );
}
